import * as React from 'react';

import {getAvatarUrl} from 'nutshell-core/url/get-avatar-url';

/**
 * Custom hook to fetch and validate a single avatar URL
 * @param {?string} avatarUrl - The avatar URL to validate
 * @returns {?string} The validated avatar URL or null
 */
export function useValidatedAvatarUrl(avatarUrl?: string | null): string | null | undefined {
    const [validatedUrl, setValidatedUrl] = React.useState<string | null | undefined>(null);

    React.useEffect(() => {
        if (avatarUrl) {
            getAvatarUrl(avatarUrl).then((url) => {
                setValidatedUrl(url);
            });
        } else {
            setValidatedUrl(null);
        }
    }, [avatarUrl]);

    return validatedUrl;
}

type EntityWithAvatar = {
    id: string;
    avatarUrl?: string | null | undefined;
};

/**
 * Custom hook to fetch and validate multiple avatar URLs for an array of entities
 * @param {Array<EntityWithAvatar>} entities - Array of entities with avatarUrl property
 * @returns {{[string]: string}} Map of entity IDs to validated avatar URLs
 */
export function useValidatedAvatarUrls<T extends EntityWithAvatar>(
    entities: T[]
): {
    [key: string]: string;
} {
    const [validatedUrls, setValidatedUrls] = React.useState<{
        [key: string]: string;
    }>({});

    React.useEffect(() => {
        if (Array.isArray(entities)) {
            const urlsToFetch = entities
                .filter((entity) => entity.avatarUrl && !validatedUrls[entity.id])
                .map((entity) => ({id: entity.id, url: entity.avatarUrl}));

            if (urlsToFetch.length > 0) {
                Promise.all(
                    urlsToFetch.map((item) => {
                        // @ts-expect-error
                        return getAvatarUrl(item.url).then((validatedUrl) => ({
                            id: item.id,
                            url: validatedUrl,
                        }));
                    })
                ).then((results) => {
                    setValidatedUrls((prev) => {
                        const newUrls = {...prev} as const;
                        results.forEach((result) => {
                            // @ts-expect-error
                            newUrls[result.id] = result.url;
                        });

                        return newUrls;
                    });
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entities]);

    return validatedUrls;
}
